/**
 * One card of the shared `WorkshopList`.
 *
 * A port of the design system's `components/WorkshopList/WorkshopList.jsx`
 * item: a coloured date block on the left, then a content column (venue,
 * time pill, sub-region/species, staff lines and seal, and a bottom sales
 * row) built by `WorkshopListContent`.
 *
 * Everything is driven from `WorkshopChip`, the shape the calendar already
 * speaks — no adapter and no lookup tables. Visibility is resolved once, up
 * front, through the pure `resolveWorkshopListVisibility`, so the markup
 * below just reads booleans; each piece then lives in its own file, both
 * because two components can't share one and because the row itself is long
 * enough to hit the file's line cap on its own.
 *
 * The card is a plain container. When it is clickable, the click lives on a
 * transparent button laid over it — see `WorkshopListOpenButton`.
 */
import { CREW_ROLE_LABELS } from '@bonsai/shared';
import type { CSSProperties, ReactElement, ReactNode } from 'react';

import { isCancelled } from './cancelled-workshops';
import { DISABLED_LABEL, isDisabled } from './disabled-workshops';
import { WorkshopListContent } from './WorkshopListContent';
import type { WorkshopListDateField } from './WorkshopListDateBlock';
import { WorkshopListDateBlock } from './WorkshopListDateBlock';
import { WorkshopListOpenButton } from './WorkshopListOpenButton';
import { workshopListRowLabel } from './workshopListRowA11y';
import type { WorkshopListSize } from './workshopListSizes';
import { WORKSHOP_LIST_SIZES } from './workshopListSizes';
import { WorkshopListTag } from './WorkshopListTag';
import type { WorkshopListRowData, WorkshopListSwitches } from './workshopListVisibility';
import { resolveWorkshopListVisibility } from './workshopListVisibility';
import { fmtTime } from './workshops-calendar-dates';
import type { WorkshopChip, WorkshopClickHandler } from './workshopsCalendarTypes';

export interface WorkshopListItemProps extends WorkshopListSwitches {
  w: WorkshopChip;
  onWorkshopClick?: WorkshopClickHandler;
  dateFields?: WorkshopListDateField[];
  size?: WorkshopListSize;
  /** Custom right-side renderer; replaces the sales row. */
  right?: (w: WorkshopChip) => ReactNode;
  background?: string;
}

/** What the row has to draw with, reduced to the booleans the pure resolver
 *  wants — split out so `WorkshopListItem` itself stays under the line cap. */
function rowData(w: WorkshopChip, timeLabel: string, hasRight: boolean): WorkshopListRowData {
  return {
    hasVenue: !!w.venueName,
    hasTime: timeLabel !== '',
    hasSubregion: !!w.subregionLabel,
    hasSpecies: !!w.species,
    hasTeacher: !!w.leadInstructor,
    hasAssistant: !!w.assistant,
    mine: !!w.mine,
    roleLabel: w.myRole ? CREW_ROLE_LABELS[w.myRole] : null,
    hasSeal: (w.replacements ?? 0) > 0,
    hasCapacity: w.capacity > 0,
    // Pacing means nothing for a workshop that won't run.
    hasStatus: !!w.status && !isCancelled(w),
    hasRight,
  };
}

function rowStyle(
  z: (typeof WORKSHOP_LIST_SIZES)['md'],
  ring: boolean,
  background: string | undefined,
): CSSProperties {
  return {
    display: 'flex',
    gap: z.gap,
    background: background ?? 'var(--nb-workshop-card)',
    borderRadius: z.radius,
    padding: z.pad,
    minHeight: z.minH,
    boxShadow: ring
      ? 'var(--nb-row-shadow-ring), 0 0 0 2px var(--nb-dark-leaf)'
      : 'var(--nb-row-shadow)',
    position: 'relative',
    // Makes this card the stacking context the open button and a caller's
    // `right` content are layered in. Without it their z-indexes resolve
    // against whatever context the surrounding page happens to provide, and
    // "the button sits under the controls" holds only by luck.
    isolation: 'isolate',
  };
}

export function WorkshopListItem({
  w,
  onWorkshopClick,
  dateFields,
  size = 'md',
  right,
  background,
  ...switches
}: WorkshopListItemProps): ReactElement {
  // Venue-local wall clock — never re-derived from the instant.
  const local = w.localDate ?? w.date;
  const timeLabel = fmtTime(local);
  const z = WORKSHOP_LIST_SIZES[size];

  const visibility = resolveWorkshopListVisibility(switches, rowData(w, timeLabel, !!right));
  const cancelled = isCancelled(w);

  return (
    <div
      data-testid={`workshop-list-item-${w.id}`}
      style={rowStyle(z, visibility.ring, background)}
    >
      {onWorkshopClick && (
        <WorkshopListOpenButton
          label={workshopListRowLabel(w.venueName, local, timeLabel, cancelled)}
          radius={z.radius}
          onActivate={() => onWorkshopClick(w)}
        />
      )}
      {cancelled ? (
        <WorkshopListTag show label="Cancelled" danger />
      ) : isDisabled(w) ? (
        <WorkshopListTag show label={DISABLED_LABEL} info />
      ) : (
        <WorkshopListTag show={visibility.tag} label={visibility.tagLabel} />
      )}
      <WorkshopListDateBlock
        local={local}
        color={w.accentColor}
        size={size}
        dateFields={dateFields}
      />
      <WorkshopListContent
        w={w}
        timeLabel={timeLabel}
        visibility={visibility}
        right={right}
        size={size}
      />
    </div>
  );
}
